import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { attendanceKeys } from "@/features/attendance/api";
import { memberKeys } from "@/features/team/api";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapEmpty } from "@/shared/api/empty";
import { openFile, uploadFile } from "@/shared/api/files";
import { makeKeys } from "@/shared/api/queryKeys";

export type EmployeeCard = Schemas["EmployeeCardOut"];
export type Employee = Schemas["EmployeeOut"];
export type EmployeeCreate = Schemas["EmployeeCreate"];
export type EmployeeUpdate = Schemas["EmployeeUpdate"];
export type MyEmployeeUpdate = Schemas["MyEmployeeUpdate"];
export type OrgNode = Schemas["OrgNodeOut"];
export type JobEvent = Schemas["JobEventOut"];
export type JobEventKind = Schemas["JobEventKind"];
export type JobChangeCreate = Schemas["JobChangeCreate"];
export type JobChangeKind = JobChangeCreate["kind"];
export type EmployeeDocument = Schemas["DocumentOut"];
export type DocumentKind = Schemas["DocumentKind"];
export type DocumentUpdate = Schemas["DocumentUpdate"];
export type Certification = Schemas["CertificationOut"];
export type CertificationFields = Schemas["CertificationFields"];
export type ContractType = Schemas["ContractType"];
export type WorkMode = Schemas["WorkMode"];
export type HoursPolicy = Schemas["HoursPolicy"];
export type EmploymentStatus = Schemas["EmploymentStatus"];
export type Person = Schemas["PersonOut"];
export type Accommodation = Schemas["AccommodationOut"];
export type AccommodationInput = Schemas["AccommodationIn"];
export type AccommodationKind = Schemas["AccommodationKind"];

export const CONTRACT_TYPES: readonly ContractType[] = ["full_time", "part_time", "freelance"];
export const WORK_MODES: readonly WorkMode[] = ["office", "hybrid", "remote"];
export const HOURS_POLICIES: readonly HoursPolicy[] = ["flexible", "fixed"];
export const DOCUMENT_KINDS: readonly DocumentKind[] = [
  "contract",
  "id_card",
  "cv",
  "certificate",
  "medical",
  "other",
];
export const JOB_CHANGE_KINDS: readonly JobChangeKind[] = [
  "promoted",
  "transferred",
  "manager_changed",
  "contract_changed",
];

export const employeeKeys = makeKeys("employees");
export const orgChartKeys = makeKeys("org-chart");
export const timelineKeys = makeKeys("employee-timeline");
export const documentKeys = makeKeys("employee-documents");
export const certificationKeys = makeKeys("employee-certifications");
export const accommodationKeys = makeKeys("employee-accommodations");
export const myEmployeeKey = ["my-employee"] as const;

/* The whole directory in one list (status=all): the hub filters and counts it in place. */
const DIRECTORY_KEY = employeeKeys.list({ status: "all" });

/** Everyone on the staff with their HR facts, by name (HR sees every status). */
export function useDirectory() {
  return useQuery({
    queryKey: DIRECTORY_KEY,
    queryFn: () => unwrap(api.GET("/api/v1/employees", { params: { query: { status: "all" } } })),
  });
}

export function useOrgChart() {
  return useQuery({
    queryKey: orgChartKeys.list(),
    queryFn: () => unwrap(api.GET("/api/v1/org-chart")),
  });
}

/** The code a new record gets by default (404-007); asked fresh each time the form opens. */
export function useNextEmployeeCode(enabled: boolean) {
  return useQuery({
    queryKey: [...employeeKeys.all, "next-code"],
    queryFn: () => unwrap(api.GET("/api/v1/employees/next-code")),
    enabled,
    staleTime: 0,
  });
}

export function useEmployee(employeeId: string) {
  return useQuery({
    queryKey: employeeKeys.detail(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

/** Whatever shows a person's job facts elsewhere: the directory, the chart, the team. */
function refreshPeople(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: employeeKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: orgChartKeys.all }),
    queryClient.invalidateQueries({ queryKey: memberKeys.all }),
  ]);
}

function storeEmployee(queryClient: QueryClient, saved: Employee): Promise<unknown> {
  queryClient.setQueryData(employeeKeys.detail(saved.user_id), saved);
  return Promise.all([
    refreshPeople(queryClient),
    // Edits to the manager, the contract or the last day reach the timeline.
    queryClient.invalidateQueries({ queryKey: timelineKeys.detail(saved.user_id) }),
    queryClient.invalidateQueries({ queryKey: myEmployeeKey }),
  ]);
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmployeeCreate) => unwrap(api.POST("/api/v1/employees", { body })),
    onSuccess: (created) => storeEmployee(queryClient, created),
  });
}

export interface EmployeeEdit {
  changes: EmployeeUpdate;
  /** The version the form started from: someone else's save in between is a conflict. */
  version: number;
}

/**
 * Save the changed fields of a record. Pessimistic: the form waits for the server's verdict
 * (validation, a taken code, a manager loop) before it closes.
 */
export function useUpdateEmployee(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changes, version }: EmployeeEdit) =>
      unwrap(
        api.PATCH("/api/v1/employees/{employee_id}", {
          params: {
            path: { employee_id: employeeId },
            header: { "if-match": String(version) },
          },
          body: changes,
        }),
      ),
    onSuccess: (saved) => storeEmployee(queryClient, saved),
  });
}

/**
 * Read the sealed identifiers in full. Audited on the server, so asked on purpose (a mutation,
 * never refetched) and dropped from memory as soon as nothing shows it (gcTime 0).
 */
export function useRevealSensitive(employeeId: string) {
  return useMutation({
    gcTime: 0,
    mutationFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/sensitive", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

export function useTimeline(employeeId: string) {
  return useQuery({
    queryKey: timelineKeys.detail(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/timeline", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

/** A promotion, transfer, new manager or new contract; pessimistic, like every job change. */
export function useRecordJobChange(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: JobChangeCreate) =>
      unwrap(
        api.POST("/api/v1/employees/{employee_id}/job-changes", {
          params: { path: { employee_id: employeeId } },
          body,
        }),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) }),
        queryClient.invalidateQueries({ queryKey: timelineKeys.detail(employeeId) }),
        refreshPeople(queryClient),
      ]),
  });
}

/* ---- Documents ---- */

export function useDocuments(employeeId: string) {
  return useQuery({
    queryKey: documentKeys.detail(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/documents", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

export interface DocumentUpload {
  file: File;
  kind: DocumentKind;
  name: string;
  expiresOn: string | null;
}

export function useUploadDocument(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, kind, name, expiresOn }: DocumentUpload) =>
      uploadFile<EmployeeDocument>(`/api/v1/employees/${employeeId}/documents`, file, {
        method: "POST",
        fields: {
          kind,
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(expiresOn ? { expires_on: expiresOn } : {}),
        },
      }),
    onSuccess: (added) => {
      queryClient.setQueryData<EmployeeDocument[]>(documentKeys.detail(employeeId), (list) =>
        list ? [added, ...list] : list,
      );
      return queryClient.invalidateQueries({ queryKey: documentKeys.detail(employeeId) });
    },
  });
}

function setDocuments(
  queryClient: QueryClient,
  employeeId: string,
  change: (list: EmployeeDocument[]) => EmployeeDocument[],
): EmployeeDocument[] | undefined {
  const key = documentKeys.detail(employeeId);
  const previous = queryClient.getQueryData<EmployeeDocument[]>(key);
  if (previous) queryClient.setQueryData(key, change(previous));
  return previous;
}

/** Rename a document or change its kind or expiry; shown at once, undone if the save fails. */
export function useUpdateDocument(employeeId: string) {
  const queryClient = useQueryClient();
  const key = documentKeys.detail(employeeId);
  return useMutation({
    mutationFn: ({ document, changes }: { document: EmployeeDocument; changes: DocumentUpdate }) =>
      unwrap(
        api.PATCH("/api/v1/employees/{employee_id}/documents/{document_id}", {
          params: {
            path: { employee_id: employeeId, document_id: document.id },
            header: { "if-match": String(document.version) },
          },
          body: changes,
        }),
      ),
    onMutate: async ({ document, changes }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = setDocuments(queryClient, employeeId, (list) =>
        list.map((item) => (item.id === document.id ? { ...item, ...changes } : item)),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (saved) => {
      setDocuments(queryClient, employeeId, (list) =>
        list.map((item) => (item.id === saved.id ? saved : item)),
      );
    },
  });
}

export function useDeleteDocument(employeeId: string) {
  const queryClient = useQueryClient();
  const key = documentKeys.detail(employeeId);
  return useMutation({
    mutationFn: (documentId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/employees/{employee_id}/documents/{document_id}", {
          params: { path: { employee_id: employeeId, document_id: documentId } },
        }),
      ),
    onMutate: async (documentId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = setDocuments(queryClient, employeeId, (list) =>
        list.filter((item) => item.id !== documentId),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    // A certification that pointed at the file no longer does.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: certificationKeys.detail(employeeId) }),
  });
}

/** Open a document in a new tab (fetched with the session's token). */
export function openEmployeeDocument(employeeId: string, documentId: string): Promise<void> {
  return openFile(`/api/v1/employees/${employeeId}/documents/${documentId}/file`);
}

/* ---- Certifications ---- */

export function useCertifications(employeeId: string) {
  return useQuery({
    queryKey: certificationKeys.detail(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/certifications", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
  });
}

interface CertificationSave {
  /** The certification to replace; without one a new one is added. */
  certification?: Certification;
  fields: CertificationFields;
}

export function useSaveCertification(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ certification, fields }: CertificationSave) =>
      certification
        ? unwrap(
            api.PUT("/api/v1/employees/{employee_id}/certifications/{certification_id}", {
              params: {
                path: { employee_id: employeeId, certification_id: certification.id },
                header: { "if-match": String(certification.version) },
              },
              body: fields,
            }),
          )
        : unwrap(
            api.POST("/api/v1/employees/{employee_id}/certifications", {
              params: { path: { employee_id: employeeId } },
              body: fields,
            }),
          ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: certificationKeys.detail(employeeId) }),
  });
}

export function useDeleteCertification(employeeId: string) {
  const queryClient = useQueryClient();
  const key = certificationKeys.detail(employeeId);
  return useMutation({
    mutationFn: (certificationId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/employees/{employee_id}/certifications/{certification_id}", {
          params: { path: { employee_id: employeeId, certification_id: certificationId } },
        }),
      ),
    onMutate: async (certificationId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Certification[]>(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          previous.filter((item) => item.id !== certificationId),
        );
      }
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
  });
}

/* ---- Work accommodations (HR only) ---- */

/** Shorter required hours for a while: pregnancy, nursing and the like (hr.view; 403 otherwise). */
export function useAccommodations(employeeId: string, enabled = true) {
  return useQuery({
    queryKey: accommodationKeys.detail(employeeId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/employees/{employee_id}/accommodations", {
          params: { path: { employee_id: employeeId } },
        }),
      ),
    enabled,
  });
}

/** The required minutes of the days covered change: the attendance month reads them again. */
function refreshAccommodations(queryClient: QueryClient, employeeId: string): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: accommodationKeys.detail(employeeId) }),
    queryClient.invalidateQueries({ queryKey: attendanceKeys.all }),
  ]);
}

export interface AccommodationSave {
  /** The one to change (its version guards the edit); a new one when absent. */
  accommodation?: Accommodation | undefined;
  body: AccommodationInput;
}

/** Add an accommodation, or change one (hr.manage). The reason goes to the history. */
export function useSaveAccommodation(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accommodation, body }: AccommodationSave) =>
      accommodation
        ? unwrap(
            api.PUT("/api/v1/employees/{employee_id}/accommodations/{accommodation_id}", {
              params: {
                path: { employee_id: employeeId, accommodation_id: accommodation.id },
                header: { "if-match": String(accommodation.version) },
              },
              body,
            }),
          )
        : unwrap(
            api.POST("/api/v1/employees/{employee_id}/accommodations", {
              params: { path: { employee_id: employeeId } },
              body,
            }),
          ),
    onSuccess: () => refreshAccommodations(queryClient, employeeId),
  });
}

/** End an accommodation early or drop a wrong one (hr.manage), with why. */
export function useRemoveAccommodation(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accommodationId, reason }: { accommodationId: string; reason: string }) =>
      unwrapEmpty(
        api.POST("/api/v1/employees/{employee_id}/accommodations/{accommodation_id}/remove", {
          params: { path: { employee_id: employeeId, accommodation_id: accommodationId } },
          body: { reason },
        }),
      ),
    onSuccess: () => refreshAccommodations(queryClient, employeeId),
  });
}

/* ---- My record ---- */

/** The signed-in person's own record; EMPLOYEE_NOT_FOUND (404) until HR opens one. */
export function useMyEmployee() {
  return useQuery({
    queryKey: myEmployeeKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/employee")),
  });
}

export function useUpdateMyEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MyEmployeeUpdate) => unwrap(api.PATCH("/api/v1/me/employee", { body })),
    onSuccess: (saved) => {
      queryClient.setQueryData(myEmployeeKey, saved);
      return queryClient.invalidateQueries({ queryKey: employeeKeys.detail(saved.user_id) });
    },
  });
}
